import { renderCaps, type RenderCaps } from '@paladigm/answer-visuals';
import { useEffect, useState } from 'react';

import { useUIStore } from '@shared/stores/ui.store';

/**
 * The AI CIO's visual blocks are drawn by @paladigm/answer-visuals. The full
 * /ai-cio page gets the design's regular card; the docked panel the narrow one
 * while it is under 600 px, the regular one when widened past it.
 */
const PANEL_NARROW_BELOW = 600;

export type VisualWidth = 'regular' | 'narrow';

function widthFor(path: string, panelWidth: number): VisualWidth {
  if (path.startsWith('/ai-cio')) return 'regular';
  return panelWidth < PANEL_NARROW_BELOW ? 'narrow' : 'regular';
}

function pathname(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

/**
 * `client_context.render`: what this build draws, read at each send. It declares
 * `technical`, since `components/blocks/BlockRenderer.tsx` gives `AnswerVisual` the chart
 * embed (`__tests__/technical.test.tsx` checks the pairing).
 */
export function renderContext(): { render: RenderCaps } {
  const panelWidth = useUIStore.getState().aiPanelWidth;
  return { render: renderCaps({ width: widthFor(pathname(), panelWidth), technical: true }) };
}

/**
 * The card width for the place the chat is shown. Read from the location rather than the
 * router, so a visual renders wherever the chat does; following the panel's width live.
 */
export function useVisualWidth(): VisualWidth {
  const panelWidth = useUIStore((s) => s.aiPanelWidth);
  return widthFor(pathname(), panelWidth);
}

function documentTheme(): 'light' | 'dark' {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light';
}

/** The theme ThemeProvider set on <html>, followed as it changes (system included). */
export function useDocumentTheme(): 'light' | 'dark' {
  const [theme, setTheme] = useState(documentTheme);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => {
      setTheme(documentTheme());
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
    };
  }, []);
  return theme;
}
